import { useState, useEffect } from 'react'
import GalleryLightbox from '../components/GalleryLightbox'
import ContactForm from '../components/ContactForm'
import { updatePageSEO } from '../lib/seo'

export default function Projects() {
  const [pageData, setPageData] = useState(null)
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [activeImages, setActiveImages] = useState([])
  const [activeImageIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    window.scrollTo(0, 0)

    const fetchData = async () => {
      try {
        // Fetch page metadata
        const pageRes = await fetch('https://api.dev.auxo.dk/public/by-slug/projekter')
        if (pageRes.ok) {
          const data = await pageRes.json()
          setPageData(data)
          if (data.meta_title || data.meta_description) {
            updatePageSEO(data.meta_title, data.meta_description)
          }
        }

        // Fetch all project galleries
        const projectsRes = await fetch('https://api.dev.auxo.dk/public/content/galleri')
        if (projectsRes.ok) {
          const data = await projectsRes.json()
          setProjects(data)
        }
      } catch (err) {
        console.error('Error fetching projects data:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const openLightbox = (images, index) => {
    setActiveImages(images)
    setActiveIndex(index)
    setLightboxOpen(true)
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-white">
        <div className="text-center space-y-4">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-orange"></i>
          <p className="text-slate-500 font-medium">Henter projekter...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <section className="bg-[#111111] text-white py-20 relative overflow-hidden border-b border-[#222222]">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=50,format=auto/assets/c68264d9-2499-44ef-9e49-6b5cd7a6c3b2/4-53.jpg"
            alt="Projekter &amp; Referencer"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 md:px-12 text-left space-y-4">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-orange block">VORES TIDLIGERE ARBEJDE</span>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight uppercase">Projekter &amp; Referencer</h1>
          <p className="text-slate-300 max-w-2xl text-sm md:text-base leading-relaxed">
            Se vores tidligere arbejde og referencer inden for tømrerarbejde, renovering, træterrasser, penthouse-lejligheder og meget mere. Vi leverer altid førsteklasses håndværk.
          </p>
        </div>
      </section>

      {/* Projects List */}
      <section className="py-24 mx-auto max-w-7xl px-6 md:px-12 space-y-24 bg-white">
        {projects.map((p, pIdx) => (
          <div key={p.id} className="space-y-8 border-b border-slate-100 pb-20 last:border-0 last:pb-0">
            <div className="max-w-3xl space-y-4">
              <h2 className="text-2xl md:text-4xl font-extrabold text-[#111111] tracking-tight uppercase">
                {p.title}
              </h2>
              <div
                className="prose prose-slate max-w-none text-slate-600 text-sm md:text-base leading-relaxed"
                dangerouslySetInnerHTML={{ __html: p.description }}
              />
            </div>

            {/* Image Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {p.images.map((img, imgIdx) => (
                <button
                  key={imgIdx}
                  onClick={() => openLightbox(p.images, imgIdx)}
                  className="relative group aspect-square overflow-hidden rounded-none border border-slate-200 shadow-none cursor-pointer focus:outline-none"
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=300,height=300,fit=cover,quality=80,format=auto/${img.replace('https://assets.auxodev.dk/', '')}`}
                    alt={`${p.title} - Billede ${imgIdx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <i className="fa-solid fa-magnifying-glass-plus text-brand-orange text-2xl"></i>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* Lightbox */}
      <GalleryLightbox
        images={activeImages}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        initialIndex={activeImageIndex}
      />

    </div>
  )
}
